import { useEffect, useState } from "react";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import "./App.css";
import LoginPage from "./components/LoginPage";
import { deliveryClientABC, deliveryClientNBC } from "./api/cmsDeliveryClient";
import NBCLayout from "./components/NBCLayout";
import ReportsLayout from "./layouts/reporting/ReportsLayout";
import Reports from "./pages/reports/Reports";
import { getAccessCodeMappings } from "./utils/accessCodeMappings";
import RegionDetails from "./pages/reports/RegionDetails";
import ZoneDetails from "./pages/reports/ZoneDetails";
import DistrictDetail from "./pages/reports/DistrictDetails";
import Details from "./pages/reports/Details";
import NBCDetails from "./components/NBCDetails";
import Enrollment from "./components/Enrollment";
import { UserContext } from "./common/UserContext";
import { UserData } from "./models/User";
import { PrivateRoutes } from "./common/PrivateRoutes";
import CandidateReport from "./pages/reports/CandidateReport";
import FieldEnrollReport from "./pages/reports/FieldEnrlReport";
import CorporateEnrollReport from "./pages/reports/CorporateEnrollReport";
import DistributorAndVendorReport from "./pages/reports/DistributorAndVendorReport";
import SBRReport from "./pages/reports/SBRReport";
import TravellerReport from "./pages/reports/TravellerReport";
import Home from "./pages/Landing/home";
import ABCLayout from "./layouts/header/ABCLayout";
import ABCDetails from "./pages/ABC/ABCDetails";
import Dashboard from "./pages/Dashboard/Dashboard";
import ReportLogin from "./pages/ReportLogin/ReportLogin";
import DBLoginPage from "./pages/DBUpdateLogin/DBLoginPage";
import DealerDataUpdate from "./pages/DBUpdate/DealerDataUpdate";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

function App() {
  const [loginPageData, setLoginPageData] = useState<any>(null);
  const [pageLayoutElements, setPageLayoutElements] = useState<any>(null);
  const [abcLoginPageData, setABCLoginPageData] = useState<any>(null);
  const [abcPageLayoutElements, setABCPageLayoutElements] = useState<any>(null);
  const [reportLoginPageData, setReportLoginPageData] = useState<any>(null);

  const [userData, updateUserData] = useState<UserData>(() => {
    try {
      const details = localStorage.getItem("userdetail");
      if (details) {
        const parsed = JSON.parse(details);
        const userdetail = parsed.dealerInfo || {};
        return {
          isDealer: userdetail?.isDealer ?? false,
          executiveManager: userdetail?.executiveManager ?? "",
          generalManager: userdetail?.generalManager ?? "",
          authorizationPolicy: parsed.authorizationPolicy ?? [],
          accessCode: localStorage.getItem("accessCode") ?? userdetail?.accessCode ?? "",
        } as UserData;
      }
    } catch (e) {
      // ignore and fall back to defaults
    }
    return {
      isDealer: false,
      executiveManager: "",
      generalManager: "",
      authorizationPolicy: [],
      accessCode: "",
    };
  });
  const [hasReportAccess, setHasReportAccess] = useState(false);
  const [hasCorporateReportAccess, setHasCorporateReportAccess] = useState(false);

  const setUserData = (data: UserData) => {
    updateUserData(data);
  };

  const value = { userData, setUserData };

  // CMS-driven: check reporting access for ALL access codes via access_code_mapping
  useEffect(() => {
    if (!userData.accessCode) return;
    const accessCode = userData.accessCode.toLowerCase();
    getAccessCodeMappings(deliveryClientNBC).then((mappings) => {
      const config = mappings.get(accessCode);
      setHasReportAccess(config?.hasReportingAccess ?? false);
      // Corporate sub-routes: check authorizationPolicy OR CMS roleName
      const isCorporateFromPolicy = userData.authorizationPolicy?.includes("corporate") ?? false;
      const isCorporateFromRole = config?.roleName?.toLowerCase() === "corporate";
      setHasCorporateReportAccess(isCorporateFromPolicy || isCorporateFromRole);
    });
  }, [userData.accessCode, userData.authorizationPolicy]);

  // Retrieves the Website root item and saves the linked Landing page
  useEffect(() => {
    const userdetails = localStorage.getItem("userdetail");
    if (userdetails) {
      let userdetail = JSON.parse(userdetails).dealerInfo;
      const storedAccessCode = localStorage.getItem("accessCode") ?? "";
      setUserData({
        ...userdetail,
        accessCode: storedAccessCode || userdetail?.accessCode || "",
      });
    }

    deliveryClientNBC
      .items()
      .inFilter("system.type", ["login", "page_layout", "report_login"])
      .toPromise()
      .then((response) => {
        const loginData = response.data.items.filter((w) => w.system.type === "login")[0];
        const layoutData = response.data.items.filter((w) => w.system.type === "page_layout")[0];
        const reportLoginData = response.data.items.filter((w) => w.system.type === "report_login")[0];
        setLoginPageData(loginData);
        setPageLayoutElements(layoutData);
        setReportLoginPageData(reportLoginData);
      });

    deliveryClientABC
      .items()
      .inFilter("system.type", ["login", "page_layout"])
      .toPromise()
      .then((response) => {
        const abcLoginData = response.data.items.filter((w) => w.system.type === "login")[0];
        const abcLayoutData = response.data.items.filter((w) => w.system.type === "page_layout")[0];
        setABCLoginPageData(abcLoginData);
        setABCPageLayoutElements(abcLayoutData);
      });
  }, []);

  return (
    <UserContext.Provider value={value}>
      <BrowserRouter>
        <Routes>
          <Route index element={<Home />} />
          <Route path="/nbc" element={<LoginPage loginPageData={loginPageData} headerPageData={pageLayoutElements} eventTypes="nbc" />} />
          <Route path="/reportLogin" element={<ReportLogin loginPageData={reportLoginPageData} headerPageData={pageLayoutElements} eventTypes="nbc" />} />
          <Route path="/abc" element={<LoginPage loginPageData={abcLoginPageData} headerPageData={abcPageLayoutElements} eventTypes="abc" />} />
          <Route path="/auth" element={<DBLoginPage />} />
          <Route path="/dealerdataupdate" element={<DealerDataUpdate />} />
          <Route element={<PrivateRoutes />}>
            <Route path="abc" element={<ABCLayout />}>
              <Route index path="welcome" element={<ABCDetails />}></Route>
            </Route>
            <Route path="nbc" element={<NBCLayout />}>
              <Route path="dashboard" element={<Dashboard />}> </Route>
              <Route index path="welcome" element={<NBCDetails />}></Route>
              <Route path="enrollment" element={<Enrollment />}></Route>
              {hasReportAccess && (
                <Route path="reporting" element={<ReportsLayout />}>
                  <Route index element={<Reports />} />
                  <Route path="region" element={<RegionDetails />} />
                  <Route path="region/:region/zone" element={<ZoneDetails />} />
                  <Route path="region/:region/zone/:zone/district" element={<DistrictDetail />} />
                  <Route path="region/:region/zone/:zone/district/:id" element={<Details />} />
                  <Route path="candidate" element={<CandidateReport />} />
                  <Route path="field" element={<FieldEnrollReport />} />
                  {!hasCorporateReportAccess && <Route path="*" element={<Navigate to={"/"} />} />}
                  {hasCorporateReportAccess && (
                    <>
                      <Route path="corporate" element={<CorporateEnrollReport />} />
                      <Route path="distributor" element={<DistributorAndVendorReport />} />
                      <Route path="sbr" element={<SBRReport />} />
                      <Route path="traveller" element={<TravellerReport />} />
                      <Route path="*" element={<Navigate to={"/"} />} />
                    </>
                  )}
                </Route>
              )}
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <ToastContainer position="top-right" autoClose={3000} />
    </UserContext.Provider>
  );
}

export default App;
